// ============================================================================
// IMAGE ATTACHMENT CONFIGURATION
// ============================================================================
// Set this to true to enable image attachment functionality globally
// Set this to false to disable image attachment functionality globally
const ENABLE_IMAGE_ATTACHMENT = false;

import {
  Component,
  ElementRef,
  Inject,
  OnInit,
  Renderer2,
  ViewChild,
  OnDestroy,
  Input,
  EventEmitter,
  Output,
  ChangeDetectorRef,
} from '@angular/core';
import { ChatContainerComponent } from '../../chat-container/chat-container.component';
import { ChatFacadeService } from '@domgen/smart-fix/data-access-smart-fix';
import { SmartFixAnalyticsService } from '../../../services/smart-fix-analytics/smart-fix-analytics.service';
import { AppConfig, Claim } from '@domgen/dgx-components';
import { CONFIG } from '@domgen/dgx-fe-config';
import { ApiService } from '@domgen/data-access-claims';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { Observable, Subject, takeUntil } from 'rxjs';
import { selectedModel, getInitialPromptByProductType } from '../utils-bedrock';
import { initialPromptChatWithDocs } from '../utils-bedrock';
import * as z from 'zod';
import posthog from 'posthog-js';

interface SelectedImage {
  file: File;
  preview: string;
}

interface AttachedImage {
  filename: string;
  mimeType: string;
  data: string; // base64 encoded
}

type BedrockMessages = {
  role: 'user' | 'agent';
  sessionId: string;
  content: {
    message: string;
    type: string;
    timestamp: string;
    images?: AttachedImage[];
  };
};

// Zod schemas for Bedrock response validation
const BedrockContentSchema = z.object({
  type: z.string(),
  text: z.string(),
});

const BedrockImageAnalysisSchema = z
  .object({
    content: z.array(BedrockContentSchema),
    role: z.string().optional(),
    type: z.string().optional(),
  })
  .catchall(z.unknown());

// Schema for when guardrail is triggered
const BedrockGuardrailSchema = z
  .object({
    'amazon-bedrock-guardrailAction': z.literal('INTERVENED'),
  })
  .catchall(z.unknown());

// Schema for successful parsing of image analysis content
const ImageContentSchema = z
  .object({
    summary: z.string().optional(),
    visibleClues: z.array(z.string()).optional(),
  })
  .catchall(z.unknown());

@Component({
  selector: 'smart-fix-bedrock-chat-container',
  templateUrl: './bedrock-chat-container.component.html',
  styleUrls: [
    './bedrock-chat-container.component.scss',
    './../../chat-container/chat-container.component.scss',
  ],
})
export class BedrockChatContainerComponent
  extends ChatContainerComponent
  implements OnInit, OnDestroy
{
  history: Array<BedrockMessages> = [];
  message: string = '';
  showDevMode = true;
  sessionId!: string;
  initialMessage: string = 'Hello';
  private destroy$ = new Subject<void>();
  @Input() claim!: Claim;
  @Input() title!: string;

  @Output() selectedSessionId = new EventEmitter<string>();

  showImagePrompt: boolean = false;

  selectedImages: SelectedImage[] = [];
  maxImages: number = 1;
  maxFileSize: number = 5 * 1024 * 1024; // 5MB
  enableImageAttachment: boolean = ENABLE_IMAGE_ATTACHMENT;

  showTopGradient: boolean = false;
  isScrollable: boolean = false;
  skipNextResult = false; // need this to allow a clear when listening is still active
  private finalSpeechTranscript = '';
  private interimSpeechTranscript = '';

  // Flag to prevent auto-sending when images are attached via smart-fix-image-attach-component
  private preventAutoSend: boolean = false;

  modelOptions: { label: string; value: string }[] = [
    {
      label: 'Claude 3 Haiku',
      value:
        'arn:aws:bedrock:eu-west-2::foundation-model/anthropic.claude-3-haiku-20240307-v1:0',
    },
    {
      label: 'Claude 3 Sonnet',
      value:
        'arn:aws:bedrock:eu-west-2::foundation-model/anthropic.claude-3-sonnet-20240229-v1:0',
    },
    {
      label: 'Claude 3.5 Sonnet',
      value:
        'arn:aws:bedrock:eu-west-2::foundation-model/anthropic.claude-3-5-sonnet-20241022-v2:0',
    },
  ];

  initForm?: FormGroup;
  isFadeIn = false;
  isLoading = false;
  @ViewChild('fileInput') fileInput!: ElementRef;
  @ViewChild('textArea') textArea!: ElementRef<HTMLTextAreaElement>;
  @ViewChild('scrollContainer') scrollContainerRef!: ElementRef;
  isListening: boolean = false;
  recognition: any;
  isSpeechRecognized: boolean = false;
  isInputActive: boolean = false;
  enablePromptEditingForSmartFix: boolean = false;

  answers: string[] = [];
  currentPrompt = '';
  gatheringContextActive = false;
  summary: string = '';

  selectedMode: string | null = null;
  // Guard to prevent multiple calls to load model list
  modelsLoaded: boolean = false;

  // Configuration for flexible image attachment timing
  allowImageAttachmentBeforeQuestions: boolean = false;
  imagePromptPhase: 'before' | 'during' | 'after' = 'before';

  enableVoiceCapture: boolean = true;
  isFeedback: boolean = false;
  feedback: string = '';
  feedbackRequested: boolean = false;
  isInteractionStarted: boolean = false;
  isWarning: boolean = false;

  onRequestFeedback() {
    this.feedbackRequested = true;
  }
  onSendFeedback(event: string) {
    this.feedback = event;
  }

  attemptEnd() {
    this.smartFixAnalyticsService.triggerSmartFixEvent(
      'nav menu',
      'fault-details-completed',
      this.claim,
      'end-chatbot-x-clicked',
      'SF genAI',
      'claims-sf-genAI'
    );
    if (this.isInteractionStarted) {
      this.isFeedback = true;
    } else {
      this.isWarning = true;
    }
  }

  onEndChat(): void {
    this.smartFixAnalyticsService.triggerSmartFixEvent(
      'Yes, end the chat',
      'fault-details-completed',
      this.claim,
      'end-chatbot-x-clicked',
      'SF genAI',
      'claims-sf-genAI'
    );
    this.closeChatBotWithoutConfirmation();
  }

  onContinueChat(): void {
    this.smartFixAnalyticsService.triggerSmartFixEvent(
      'No, continue',
      'fault-details-completed',
      this.claim,
      'end-chatbot-x-clicked',
      'SF genAI',
      'claims-sf-genAI'
    );
    this.isWarning = false;
  }

  chatForm = new FormGroup({
    message: new FormControl('', Validators.compose([Validators.required])),
  });

  constructor(
    chatFacade: ChatFacadeService,
    smartFixAnalyticsService: SmartFixAnalyticsService,
    private apiService: ApiService,
    renderer: Renderer2,
    private cdr: ChangeDetectorRef,
    @Inject(CONFIG) protected readonly config: AppConfig
  ) {
    super(chatFacade, smartFixAnalyticsService, config, renderer);
    this.enablePromptEditingForSmartFix =
      this.config.runtimeFeatures.enablePromptEditingForSmartFix;

    this.enableVoiceCapture =
      this.config.runtimeFeatures.enableVoiceCapture && this.isOnChrome; // check on chrome
  }

  /** Check if current product type is a boiler */
  get isBoilerProduct(): boolean {
    const productType = this.claim.getData?.ProductType?.toLowerCase() || '';
    const trimmedType = productType.trim();

    // Return false if explicitly marked as non-boiler
    if (trimmedType.includes('non-boiler')) {
      return false;
    }

    // Return true if contains boiler
    return trimmedType.includes('boiler');
  }

  ngOnInit(): void {
    // Always enable mode selection - let users choose between rag, docs, and agent-llm
    if (this.enablePromptEditingForSmartFix) {
      this.initiateForm();
      this.loadModelList();
      // When prompt editing is enabled, set up the form but don't auto-start
      this.initForm?.patchValue({
        prompt: getInitialPromptByProductType(this.claim.getData?.ProductType),
      });
    } else {
      // When prompt editing is disabled, auto-start with triage questions
      this.useLLMBotFunctionality();
    }

    // Set RAG as the default mode for all product types
    this.selectedMode = 'rag';
  }

  /**
   * Flexible LLM Bot Functionality with configurable image attachment timing
   *
   * Special Handling for Boiler Product Types:
   * - If productType includes 'boiler', automatically enables image attachment before questions
   * - Uses 'rag' mode for better solutions with boiler-specific context
   * - After triage questions are completed, passes the summary as the first message to RAG solutions
   *
   * Usage Examples:
   *
   * 1. To enable image attachment BEFORE questions:
   *    this.enableImageAttachmentBeforeQuestions();
   *    this.useLLMBotFunctionality();
   *
   * 2. To enable image attachment AFTER questions (default behavior):
   *    this.enableImageAttachmentAfterQuestions(); // or don't call anything as this is default
   *    this.useLLMBotFunctionality();
   *
   * 3. To disable image attachment entirely:
   *    this.disableImageAttachment();
   *    this.useLLMBotFunctionality();
   *
   * 4. To configure custom timing:
   *    this.configureImageAttachment('before', true);
   *    this.useLLMBotFunctionality();
   */
  useLLMBotFunctionality(): void {
    // Check if we should show image prompt before gathering context
    if (
      ENABLE_IMAGE_ATTACHMENT &&
      this.allowImageAttachmentBeforeQuestions &&
      this.imagePromptPhase === 'before'
    ) {
      this.showImagePromptBeforeQuestions();
      return;
    }

    this.gatherContextFromUser();
  }

  private startBoilerTriageFlow(): void {
    // For boiler types, always start with image attachment first, then immediately start triage questions
    this.showImagePromptBeforeQuestions();
  }

  private startBoilerTriageFlowWithoutImages(): void {
    // For boiler types when images are disabled, go directly to triage questions
    this.gatherContextFromUser();
  }

  private showImagePromptBeforeQuestions(): void {
    if (!this.enableImageAttachment) {
      return;
    }
    this.showImagePrompt = true;

    // Check if this is a boiler type for better messaging
    const productType = this.claim.getData?.ProductType?.toLowerCase() || '';
    const isBoiler = productType.trim().includes('boiler');

    const message = isBoiler
      ? 'Hi Chris 👋 Can you please upload a photo of your boiler? Try to get all of it in the picture if you can.'
      : 'Please attach an image of the issue to help me provide more accurate assistance.';

    this.history.push({
      role: 'agent',
      sessionId: this.sessionId || 'unknown',
      content: {
        message,
        type: 'text',
        timestamp: new Date().toISOString(),
      },
    });
  }

  private gatherContextFromUser(): void {
    this.isLoading = true;
    this.apiService
      .gatherContextFromUser({
        answers: this.answers,
        productType: this.claim.getData?.ProductType.toLowerCase() || '',
        brand: this.claim.getData?.BookingOEM?.toLowerCase() || '',
      })
      .subscribe({
        next: (res) => {
          this.isLoading = false;
          this.currentPrompt = res.reAsk || res.nextQuestion || '';

          if (res.greeting) {
            this.history.push({
              role: 'agent',
              sessionId: this.sessionId || 'unknown',
              content: {
                message: this.isBoilerProduct
                  ? res.greeting
                  : `Hi ${this.claim.reflect?.firstName || 'there'} 👋 `,
                type: 'text',
                timestamp: new Date().toISOString(),
              },
            });
          }
          this.answers = res.answers || [];
          this.gatheringContextActive = !res.done;

          // Add current prompt to history
          this.history.push({
            role: 'agent',
            sessionId: this.sessionId || 'unknown',
            content: {
              message: this.currentPrompt,
              type: 'text',
              timestamp: new Date().toISOString(),
            },
          });

          // Handle context gathering completion
          if (res.done) {
            this.handleContextGatheringComplete(res);
          }
        },
        error: (error) => {
          console.error('Error gathering context from user:', error);
          this.isLoading = false;
          this.history.push({
            role: 'agent',
            sessionId: this.sessionId || 'unknown',
            content: {
              message:
                'Sorry, I encountered an error while gathering information. Please try again.',
              type: 'text',
              timestamp: new Date().toISOString(),
            },
          });
        },
      });
  }

  private handleContextGatheringComplete(res: any): void {
    // Preserve existing summary content (like image analysis) and append triage summary
    const triageSummary = res?.summary || '';
    if (this.summary && triageSummary) {
      this.summary = this.summary + ' ' + triageSummary;
    } else {
      this.summary = triageSummary;
    }
    this.gatheringContextActive = false;

    if (res.done) {
      this.initiateBedrockChat(this.summary);
      return;
    }

    // Show image prompt based on configuration if not done
    if (this.imagePromptPhase === 'after') {
      this.showImagePrompt = true;
      this.history.push({
        role: 'agent',
        sessionId: this.sessionId || 'unknown',
        content: {
          message:
            'Would you like to attach an image of the issue for better troubleshooting? This can help me provide more accurate assistance.',
          type: 'text',
          timestamp: new Date().toISOString(),
        },
      });
    }
  }

  // Method to continue after image attachment (before questions flow)
  continueAfterImageAttachment(): void {
    if (this.imagePromptPhase === 'before') {
      this.showImagePrompt = false;

      // Check if this is a boiler type and handle accordingly
      const productType = this.claim.getData?.ProductType?.toLowerCase() || '';
      if (productType.trim().includes('boiler')) {
        // For boiler types, start triage questions after image attachment
        this.gatherContextFromUser();
      } else {
        // For other types, continue with normal flow
        this.gatherContextFromUser();
      }
    }
  }

  // Method to configure image attachment timing
  configureImageAttachment(
    phase: 'before' | 'during' | 'after',
    allow: boolean = true
  ): void {
    // Always update the phase regardless of global setting
    this.imagePromptPhase = phase;

    // Only allow image attachment if globally enabled
    if (ENABLE_IMAGE_ATTACHMENT) {
      this.allowImageAttachmentBeforeQuestions = allow;
    } else {
      this.allowImageAttachmentBeforeQuestions = false;
    }
  }

  // Helper methods for specific configurations
  enableImageAttachmentBeforeQuestions(): void {
    this.configureImageAttachment('before', true);
  }

  enableImageAttachmentAfterQuestions(): void {
    this.configureImageAttachment('after', true);
  }

  enableImageAttachmentDuringQuestions(): void {
    this.configureImageAttachment('during', true);
  }

  disableImageAttachment(): void {
    this.allowImageAttachmentBeforeQuestions = false;
  }

  // Getter to access the global image attachment setting
  get isImageAttachmentGloballyEnabled(): boolean {
    return ENABLE_IMAGE_ATTACHMENT;
  }

  skipImageAttachment(): void {
    this.showImagePrompt = false;

    // Handle different phases of image attachment
    if (this.imagePromptPhase === 'before') {
      // If we're skipping images before questions, start context gathering
      this.gatherContextFromUser();
    } else {
      // If we're skipping images after questions, proceed to chat
      //  ensure we use RAG mode for better solutions
      this.selectedMode = 'rag';
      this.initiateBedrockChat(this.summary);
    }
  }

  promptToAttachImage(): void {
    this.enableImageAttachment = true;
  }

  private sendImagesAndContinue(images: SelectedImage[]): void {
    this.isLoading = true;
    this.updateFormControlState();

    const messageContent: BedrockMessages['content'] = {
      message: this.message ?? null,
      type: 'image',
      timestamp: new Date().toISOString(),
      images: images.map((img) => ({
        data: img.preview.split(',')[1],
        mimeType: img.file.type,
        filename: img.file.name,
      })),
    };

    this.history.push({
      role: 'user',
      sessionId: this.sessionId,
      content: messageContent,
    });

    const imageData = { base64: images[0].preview.split(',')[1] };

    this.apiService.sendBedrockChatMessageWithImage('', imageData).subscribe({
      next: (res) => {
        this.isLoading = false;
        this.updateFormControlState();
        this.showImagePrompt = false;

        console.log('Image analysis response:', JSON.stringify(res));

        this.removeImage(0);

        // Check if Amazon Bedrock guardrail was triggered using Zod
        const guardrailResult = BedrockGuardrailSchema.safeParse(res);
        if (guardrailResult.success) {
          console.log(
            'Amazon Bedrock guardrail was triggered - validated with Zod'
          );
          // Handle blocked content with custom message
          this.history.push({
            role: 'agent',
            sessionId: this.sessionId || 'unknown',
            content: {
              message:
                'Sorry, I cannot analyze this type of image. Please upload a clear image of your appliance for troubleshooting assistance.',
              type: 'text',
              timestamp: new Date().toISOString(),
            },
          });

          // Continue with the flow even if image was blocked
          const productType =
            this.claim.getData?.ProductType?.toLowerCase() || '';
          const isBoiler = productType.trim().includes('boiler');

          if (this.imagePromptPhase === 'before') {
            if (isBoiler) {
              this.gatherContextFromUser();
            } else {
              this.gatherContextFromUser();
            }
          } else {
            this.initiateBedrockChat(this.summary);
          }
          return;
        }

        // Validate the response structure with Zod
        const responseResult = BedrockImageAnalysisSchema.safeParse(res);
        if (!responseResult.success) {
          console.error(
            'Invalid Bedrock response structure:',
            responseResult.error
          );
          // Handle invalid response as error
          this.handleImageAnalysisError(new Error('Invalid response format'));
          return;
        }

        // Process content with validation
        let parsedContent = null;
        if (
          responseResult.data.content &&
          responseResult.data.content[0] &&
          responseResult.data.content[0].text
        ) {
          try {
            const contentText = responseResult.data.content[0].text;
            const contentResult = ImageContentSchema.safeParse(
              JSON.parse(contentText)
            );

            if (contentResult.success) {
              parsedContent = contentResult.data;
              console.log('Parsed content (validated):', parsedContent);
              console.log('Visible clues:', parsedContent.summary);
            } else {
              console.error('Invalid content structure:', contentResult.error);
              parsedContent = {
                summary: 'Unable to validate image content',
                visibleClues: ['Image validation failed'],
              };
            }
          } catch (error) {
            console.error('Error parsing JSON content:', error);
            parsedContent = {
              summary: 'Unable to parse image content',
              visibleClues: ['Image analysis failed'],
            };
          }
        }

        // Format visible clues and summary for LLM understanding
        let imageAnalysisText = '';
        if (parsedContent) {
          const visibleClues = parsedContent.visibleClues;
          const summary = parsedContent.summary;

          if (
            visibleClues &&
            Array.isArray(visibleClues) &&
            visibleClues.length > 0
          ) {
            // Format visible clues in a clear, LLM-friendly way
            const formattedClues = visibleClues
              .filter(
                (clue) =>
                  clue && typeof clue === 'string' && clue.trim().length > 0
              )
              .map((clue) => clue.trim())
              .join(', ');

            if (formattedClues) {
              imageAnalysisText = ` Image analysis - Visible clues: ${formattedClues}`;
            }
          } else if (
            summary &&
            typeof summary === 'string' &&
            summary.trim().length > 0
          ) {
            // Fallback to summary if no visible clues
            imageAnalysisText = ` Image analysis: ${summary.trim()}`;
          }
        }

        if (imageAnalysisText) {
          this.summary = this.summary.concat(imageAnalysisText);
          console.log('Updated summary with image analysis:', this.summary);
        }

        this.history.push({
          role: 'agent',
          sessionId: this.sessionId || 'unknown',
          content: {
            message: `Thanks for the image! ${
              imageAnalysisText
                ? 'From what I can see: ' +
                  imageAnalysisText
                    .replace('Image analysis - Visible clues: ', '')
                    .replace('Image analysis: ', '') +
                  '. '
                : ''
            }This initial image analysis will help me provide better troubleshooting solutions. ${
              this.imagePromptPhase === 'before'
                ? "Now I'll ask you a few quick questions to understand the issue better."
                : "Let me provide you with some troubleshooting steps based on this analysis and the information you've provided."
            }`,
            type: 'text',
            timestamp: new Date().toISOString(),
          },
        });

        // Handle different phases after image processing
        const productType =
          this.claim.getData?.ProductType?.toLowerCase() || '';
        const isBoiler = productType.trim().includes('boiler');

        if (this.imagePromptPhase === 'before') {
          if (isBoiler) {
            // For boiler types, if images were attached before questions, now start context gathering (triage questions)
            this.gatherContextFromUser();
          } else {
            // For other types, continue with normal flow
            this.gatherContextFromUser();
          }
        } else {
          // If images were attached after questions, initiate the full Bedrock chat
          // For boiler types, this will use RAG mode with the combined summary
          this.initiateBedrockChat(this.summary);
        }
      },

      error: (error) => {
        this.handleImageAnalysisError(error);
      },
    });
  }

  initiateForm() {
    this.initForm = new FormGroup({
      prompt: new FormControl(
        getInitialPromptByProductType(this.claim.getData?.ProductType),
        Validators.compose([Validators.required])
      ),
      modelArn: new FormControl(
        selectedModel,
        Validators.compose([Validators.required])
      ),
      initialMessage: new FormControl(this.initialMessage),
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onModeSelected(mode: string) {
    // Save previous mode to check if we're just switching modes
    const previousMode = this.selectedMode;
    this.selectedMode = mode;
    this.chatForm.reset();

    // Clear any duplicate messages from history if we're just switching modes
    if (previousMode && this.history.length > 0) {
      // Remove duplicate boiler photo requests if present
      const boilerMessage =
        'Hi Chris 👋 Can you please upload a photo of your boiler?';
      const filteredHistory = this.history.filter((msg, idx, arr) => {
        if (idx === 0) return true;
        const prevMsg = arr[idx - 1];
        return !(
          msg.role === 'agent' &&
          prevMsg.role === 'agent' &&
          msg.content.message.includes(boilerMessage) &&
          prevMsg.content.message.includes(boilerMessage)
        );
      });
      this.history = filteredHistory;
    }

    // update prompt as diff mode is selected
    if (this.selectedMode === 'agent-llm') {
      this.gatheringContextActive = true;
      this.initForm?.patchValue({
        prompt: `[Prompt is controlled in agent console]
          Convo will start automatically in the bot container`,
      });

      // Example: Configure image attachment before questions for this mode
      // Uncomment the next line to enable image attachment before questions:
      // this.enableImageAttachmentBeforeQuestions();

      this.useLLMBotFunctionality();
      return;
    }

    if (this.selectedMode === 'agent-llm-image-first') {
      // Example mode that shows image attachment before questions
      this.gatheringContextActive = true;
      this.initForm?.patchValue({
        prompt: `[Prompt is controlled in agent console]
          Images will be collected first, then questions`,
      });

      this.enableImageAttachmentBeforeQuestions();
      this.useLLMBotFunctionality();
      return;
    }

    if (this.selectedMode === 'docs') {
      this.initForm?.patchValue({
        prompt: initialPromptChatWithDocs,
      });
      return;
    }

    if (this.selectedMode === 'rag') {
      this.initForm?.patchValue({
        prompt: getInitialPromptByProductType(this.claim.getData?.ProductType),
      });
      return;
    }
  }

  /**
   * Formats the summary into a natural, conversational first message for RAG
   */
  private formatSummaryForRAG(summary: string): string {
    return summary;
  }

  initiateBedrockChat(summary: string = '') {
    this.isLoading = true;
    this.updateFormControlState();

    // For all product types with summary, use the summary as the initial message
    const hasValidSummary = summary && summary.trim().length > 0;

    const initialMessage = hasValidSummary
      ? this.formatSummaryForRAG(summary)
      : this.enablePromptEditingForSmartFix
      ? this.initForm?.value.initialMessage
      : this.initialMessage;

    // If we're in RAG mode for boilers and prompt editing is enabled,
    // prefer running the triage / LLMBot flow rather than immediately
    // initiating the RAG API. This ensures triage questions (and any
    // image prompts) are collected when users manually start the chat
    // with prompt editing enabled.
    if (
      this.selectedMode === 'rag' &&
      this.enablePromptEditingForSmartFix &&
      !hasValidSummary
    ) {
      // Start the triage / LLMBot flow which will eventually call
      // initiateBedrockChat(summary) once the triage summary is ready.
      this.useLLMBotFunctionality();
      this.isLoading = false;
      this.updateFormControlState();
      return;
    }

    let apiCall$: Observable<any>;

    // call lambda depending on the mode
    // agent-llm, rag, docs
    switch (this.selectedMode) {
      case 'agent-llm':
        apiCall$ = this.apiService.startAgentConversation(
          summary,
          this.claim.reflect?.manufacturer,
          this.claim.getData?.ModelNumber || undefined,
          this.claim.reflect?.productType?.toLowerCase() || undefined,
          this.claim.reflect?.planNumber || undefined
        );
        break;
      case 'rag':
        apiCall$ = this.apiService.initiateBedrockChat(
          initialMessage,
          this.claim.reflect?.productType?.toLowerCase(),
          this.claim.reflect?.planNumber,
          this.initForm?.value.prompt || undefined,
          this.initForm?.value.modelArn || undefined
        );
        break;
      case 'docs':
        apiCall$ = this.apiService.initiateBedrockChatChatWithDocs(
          initialMessage,
          this.claim.reflect?.productType?.toLowerCase(),
          this.claim.reflect?.planNumber,
          this.initForm?.value.prompt || undefined,
          this.initForm?.value.modelArn || undefined
        );
        break;
      default:
        console.error('no mode selected');
        return;
    }

    apiCall$.pipe(takeUntil(this.destroy$)).subscribe({
      next: (res: any) => {
        this.isLoading = false;
        this.updateFormControlState();
        this.sessionId = res.sessionId;
        this.selectedSessionId.emit(this.sessionId);
        this.history.push({
          role: 'agent',
          sessionId: res.sessionId,
          content: {
            message: res.response || res.output.text,
            type: 'text',
            timestamp: new Date().toISOString(),
          },
        });
        this.smartFixAnalyticsService.triggerSmartFixEvent(
          'success',
          'fault-details-completed',
          this.claim,
          'chatbot-loaded',
          'SF genAI',
          'claims-sf-genAI'
        );
      },
      error: (error) => {
        console.error('Error initiating Bedrock chat:', error);
        this.isLoading = false;
        this.updateFormControlState();
        this.history.push({
          role: 'agent',
          sessionId: this.sessionId || 'unknown',
          content: {
            message:
              'Sorry, I encountered an error while starting the chat. Please try again.',
            type: 'text',
            timestamp: new Date().toISOString(),
          },
        });
      },
    });
  }

  loadModelList() {
    if (this.modelsLoaded) {
      return;
    }

    this.apiService
      .listBedrockModels()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (models) => {
          models.forEach((model) =>
            this.modelOptions.push({
              label: model.modelName,
              value: model.modelArn,
            })
          );
          // mark as loaded so subsequent calls do nothing
          this.modelsLoaded = true;
        },
        error: (error) => {
          console.error('Error loading model list:', error);
          // Fail silently for model list loading as it's not critical
        },
      });
  }

  onSubmit(event: Event) {
    event.preventDefault();
    if (this.isListening && this.recognition) {
      this.recognition.stop();
      this.isListening = false;
      this.skipNextResult = true;
    }

    if (this.enableImageAttachment && this.selectedImages.length > 0) {
      this.sendImagesAndContinue(this.selectedImages);
      return;
    }
    const userMessage = this.chatForm?.value?.message?.trim();
    if (!userMessage) return;

    this.smartFixAnalyticsService.triggerSmartFixEvent(
      'clicked',
      '',
      this.claim,
      'arrow-up icon',
      'smart-fix-virtual-assistant',
      'claims-sf-virtual-assistant'
    );

    if (this.gatheringContextActive) {
      this.handleTriageStep(userMessage);
    } else if (
      this.chatForm.valid &&
      this.chatForm?.value?.message?.trim() &&
      !this.gatheringContextActive
    ) {
      this.isInteractionStarted = true;
      event.preventDefault();
      this.sendBedrockChatMessage(
        this.sessionId,
        this.chatForm?.value?.message ?? '',
        this.initForm?.value?.prompt || undefined,
        this.initForm?.value?.modelArn || undefined
      );
      this.chatForm.reset();
      this.scrollToBottom();
      this.isInputActive = false;
      this.isSpeechRecognized = false;
    }
    this.autoResize();
  }

  handleTriageStep(userMessage: string): void {
    this.chatForm.reset();

    this.history.push({
      role: 'user',
      sessionId: this.sessionId || 'unknown',
      content: {
        message: userMessage,
        type: 'text',
        timestamp: new Date().toISOString(),
      },
    });

    this.apiService
      .gatherContextFromUser({
        answers: this.answers,
        answer: userMessage,
        productType: this.claim.getData?.ProductType,
      })
      .subscribe({
        next: (res) => {
          this.answers = res.answers || [];

          if (res.done) {
            this.gatheringContextActive = false;
            // Preserve existing summary content (like image analysis) and append triage summary
            const triageSummary = res.summary || '';
            if (this.summary && triageSummary) {
              this.summary = this.summary + ' ' + triageSummary;
            } else {
              this.summary = triageSummary;
            }

            this.initiateBedrockChat(this.summary);
            return;
          }

          if (res.reAsk) {
            this.currentPrompt = res.reAsk;
            this.history.push({
              role: 'agent',
              sessionId: this.sessionId || 'unknown',
              content: {
                message: this.currentPrompt,
                type: 'text',
                timestamp: new Date().toISOString(),
              },
            });
            return;
          }

          if (res.nextQuestion) {
            this.currentPrompt = res.nextQuestion;
            this.history.push({
              role: 'agent',
              sessionId: this.sessionId || 'unknown',
              content: {
                message: this.currentPrompt,
                type: 'text',
                timestamp: new Date().toISOString(),
              },
            });
            return;
          }
        },
        error: (error) => {
          console.error('Error handling triage step:', error);
          this.history.push({
            role: 'agent',
            sessionId: this.sessionId || 'unknown',
            content: {
              message:
                'Sorry, I encountered an error while processing your answer. Please try again.',
              type: 'text',
              timestamp: new Date().toISOString(),
            },
          });
        },
      });
  }

  toggleMinimize() {
    super.toggleMinimize();
  }

  closeChat(): void {
    super.closeChat();
    this.smartFixAnalyticsService.clearSmartFixRoutingCookie();

    // If no session ID, redirect back to MMR page instead of home
  }

  sendBedrockChatMessage(
    sessionId: string,
    message: string,
    prompt?: string,
    modelArn?: string
  ) {
    this.history.push({
      role: 'user',
      sessionId: this.sessionId,
      content: {
        message,
        type: 'text',
        timestamp: new Date().toISOString(),
      },
    });

    this.isLoading = true;
    this.updateFormControlState();
    let apiCall$: Observable<any>;

    switch (this.selectedMode) {
      case 'agent-llm':
        this.initForm?.patchValue({
          prompt: '[Prompt is controlled in agent console]',
        });
        apiCall$ = this.apiService.continueAgentConversation(
          sessionId,
          message
        );
        break;
      case 'rag':
        apiCall$ = this.apiService.sendBedrockChatMessage(
          sessionId,
          message,
          prompt,
          modelArn
        );
        break;
      case 'docs':
        apiCall$ = this.apiService.sendBedrockChatMessageChatWithDocs(
          sessionId,
          message,
          prompt,
          modelArn
        );
        break;
      default:
        console.error('no mode selected');
        return;
    }

    apiCall$.pipe(takeUntil(this.destroy$)).subscribe({
      next: (res) => {
        this.sessionId = res.sessionId;
        this.isLoading = false;
        this.updateFormControlState();
        this.history.push({
          role: 'agent',
          sessionId: res.sessionId,
          content: {
            message: res.response || res.output.text,
            type: 'text',
            timestamp: new Date().toISOString(),
          },
        });
      },
      error: (error) => {
        console.error('Error sending Bedrock chat message:', error);
        this.isLoading = false;
        this.updateFormControlState();
        this.history.push({
          role: 'agent',
          sessionId: this.sessionId || 'unknown',
          content: {
            message:
              'Sorry, I encountered an error while processing your message. Please try again.',
            type: 'text',
            timestamp: new Date().toISOString(),
          },
        });
      },
    });

    this.chatForm.reset();
  }

  onFocusInput() {
    this.isInputActive = true;
  }

  onBlurInput() {
    this.isInputActive = false;
  }

  private initializeBoilerSession(): void {
    // For boilers, start LLM functionality with image attachment first
    // SessionId will be set later when we call the backend APIs
    this.useLLMBotFunctionality();
  }

  /**
   * Centralized method for handling image analysis errors
   */
  private handleImageAnalysisError(error: Error): void {
    console.error('Error handling image analysis:', error);
    this.isLoading = false;
    this.updateFormControlState();
    this.removeImage(0);

    // Continue with flow even if image analysis fails
    this.showImagePrompt = false;
    this.history.push({
      role: 'agent',
      sessionId: this.sessionId || 'unknown',
      content: {
        message:
          "I couldn't analyze the image, but I'll provide troubleshooting solutions based on the information you provided.",
        type: 'text',
        timestamp: new Date().toISOString(),
      },
    });

    if (this.imagePromptPhase === 'before') {
      this.gatherContextFromUser();
    } else {
      // Initiate Bedrock chat even if image analysis failed
      this.initiateBedrockChat(this.summary);
    }
  }

  onEnter(event: Event) {
    event.preventDefault();
    this.onSubmit(event);
  }

  removeImage(index: number) {
    this.selectedImages.splice(index, 1);

    // Reset auto-send prevention flag if no images remain
    if (this.selectedImages.length === 0) {
      this.preventAutoSend = false;
    }
  }

  voiceCaptureToggle(): void {
    if (!this.recognition) {
      this.initialiseSpeechRecognition();
    }

    if (this.isListening) {
      this.smartFixAnalyticsService.triggerSmartFixEvent(
        'clicked',
        '',
        this.claim,
        'pause icon',
        'smart-fix-virtual-assistant',
        'claims-sf-virtual-assistant'
      );
      this.recognition.stop();
      this.isListening = false;
      return;
    }

    // ensures new recording session doesn't keep the last transcript
    try {
      this.smartFixAnalyticsService.triggerSmartFixEvent(
        'clicked',
        '',
        this.claim,
        'microphone icon',
        'smart-fix-virtual-assistant',
        'claims-sf-virtual-assistant'
      );
      this.skipNextResult = false;
      this.finalSpeechTranscript = this.textArea?.nativeElement.value || ''; // if user pauses, preserve existing message
      this.interimSpeechTranscript = '';
      this.recognition.start();
      this.isListening = true;
    } catch (error) {
      console.error('Error starting speech recognition:', error);
    }
  }

  initialiseSpeechRecognition(): void {
    const SpeechRecognition =
      (window as any).speechRecognition ||
      (window as any).webkitSpeechRecognition;
    if (!SpeechRecognition) {
      return;
    }

    this.recognition = new SpeechRecognition();
    this.recognition.lang = 'en-US';
    this.recognition.interimResults = true;
    this.recognition.maxAlternatives = 1;
    this.recognition.continuous = true;

    this.finalSpeechTranscript =
      this.textArea?.nativeElement.value ||
      this.chatForm.get('message')?.value ||
      '';

    this.recognition.onresult = (e: any) => {
      if (this.skipNextResult) {
        this.skipNextResult = false;
        return;
      }
      this.interimSpeechTranscript = '';

      for (let i = e.resultIndex, len = e.results.length; i < len; i++) {
        const transcript = e.results[i][0].transcript;

        // new speech is added to existing content without overwriting it
        if (e.results[i].isFinal) {
          this.finalSpeechTranscript += transcript;
        } else {
          this.interimSpeechTranscript += transcript;
        }
      }

      const combinedText = (
        this.finalSpeechTranscript + this.interimSpeechTranscript
      ).trim();

      this.chatForm.patchValue({ message: combinedText });

      // need to expand text area as user is speaking
      setTimeout(() => {
        if (this.textArea) {
          this.textArea.nativeElement.scrollTop =
            this.textArea.nativeElement.scrollHeight;
          this.autoResize();
        }
      }, 0);
    };

    this.recognition.onend = () => {
      this.isListening = false;
    };
  }

  /**
   * Handle image attachment from the smart-fix-image-attach-component
   */
  onImageAttached(selectedImage: SelectedImage): void {
    // Set flag to prevent auto-sending of images
    this.preventAutoSend = true;

    // Add the image to our selectedImages array
    this.selectedImages.push(selectedImage);

    // Hide the image prompt since we now have an image
    this.showImagePrompt = false;
  }

  /**
   * Handle successful image attachment notification from smart-fix-image-attach-component
   */
  onImageAttachedSuccess(success: boolean): void {
    console.log('� Image attachment success:', success);
  }

  private updateFormControlState(): void {
    if (this.isLoading) {
      this.chatForm.get('message')?.disable();
    } else {
      this.chatForm.get('message')?.enable();
    }
  }

  get isChatDisabled(): boolean {
    return this.isLoading;
  }

  onClearButton(): void {
    this.skipNextResult = true; // ignore next result so old speech doesn't reppear
    if (this.textArea) {
      this.textArea.nativeElement.value = ''; // reset
    }

    this.chatForm.patchValue({ message: '' });
    this.finalSpeechTranscript = '';
    this.interimSpeechTranscript = '';
    this.autoResize();
  }

  get isOnChrome() {
    return /chrom(e|ium)/.test(navigator.userAgent.toLowerCase());
  }

  private checkScrollable(): void {
    const containerRef = this.scrollContainerRef.nativeElement;
    if (!containerRef) return;

    this.isScrollable = containerRef.scrollHeight > containerRef.clientHeight;
    this.showTopGradient = this.isScrollable && containerRef.scrollTop > 0;

    this.cdr.markForCheck();
  }

  autoResize(): void {
    const iconContainer = document.querySelector(
      '.action-icon-container'
    ) as HTMLElement;
    iconContainer.style.transition = 'none';

    if (!this.textArea || !iconContainer) return;

    if (!this.textArea?.nativeElement.value.trim()) {
      this.resetTextAreaSize(this.textArea.nativeElement, iconContainer);

      return;
    }

    const scrollHeight = this.textArea.nativeElement.scrollHeight;
    const newHeight = Math.min(scrollHeight, 208);

    this.textArea.nativeElement.style.height = 'auto';

    this.textArea.nativeElement.style.height = `${newHeight}px`;
    this.textArea.nativeElement.style.overflowY =
      scrollHeight > newHeight ? 'scroll' : 'hidden';

    setTimeout(() => {
      this.updateIconStack(iconContainer, newHeight);
    }, 0);
  }

  private resetTextAreaSize(
    textArea: HTMLTextAreaElement,
    iconContainer: HTMLElement
  ): void {
    if (!textArea) return;
    textArea.style.height = '22px';
    textArea.style.overflowY = 'hidden';
    iconContainer.classList.remove('stacked');
  }

  private updateIconStack(iconContainer: HTMLElement, newHeight: number): void {
    const isStacked = iconContainer.classList.contains('stacked');
    const shouldStack = this.enableVoiceCapture
      ? newHeight >= 80
      : newHeight >= 40;
    if (!isStacked && shouldStack) {
      iconContainer.classList.add('stacked');
    } else if (isStacked && !shouldStack) {
      iconContainer.classList.remove('stacked');
    }
  }

  override scrollToBottom() {
    if (this.scrollContainerRef?.nativeElement) {
      this.scrollContainerRef.nativeElement.scrollTop =
        this.scrollContainerRef.nativeElement.scrollHeight;
    }

    this.checkScrollable();
  }
}
